import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { IAs2Partner } from '@core/models/as2-message.model';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import as2IntegrationService from 'services/api/as2-integration.service';
import epcisService from 'services/api/epcis.service';
import OutboundValidationPanel from 'components/integrations/OutboundValidationPanel';
import { integrationErrorMessage } from '../Integrations/integration-user-messages';

const inputClass =
    'w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#bd902e] focus:outline-none focus:ring-2 focus:ring-[#bd902e]/30 disabled:bg-slate-50';

const SCHEMA_OPTIONS = [
    { value: '1.2', label: 'EPCIS 1.2 (healthcare / production default)' },
    { value: '1.0', label: 'EPCIS 1.0' },
    { value: '2.0', label: 'EPCIS 2.0' },
];

type EpcisRow = {
    id?: number;
    documentId?: string;
    email?: string;
    productBatchId?: number;
    sourceSchemaVersion?: string;
    jsonPathPresent?: boolean;
};

function epcisOptionLabel(row: EpcisRow): string {
    const parts = [`#${row.id}`];
    if (row.documentId) parts.push(row.documentId);
    if (row.productBatchId) parts.push(`batch ${row.productBatchId}`);
    if (row.email) parts.push(row.email);
    return parts.join(' · ');
}

type EnvironmentFilter = 'ALL' | 'TEST' | 'PRODUCTION';

function isProductionPartner(p?: IAs2Partner): boolean {
    if (!p) return false;
    return p.environment !== 'TEST' && !p.code?.endsWith('_TEST');
}

interface Props {
    partners?: IAs2Partner[];
    /** Which partners to offer. Default ALL. Wizard locks this to TEST for safe onboarding. */
    environmentFilter?: EnvironmentFilter;
    /** Hide the environment selector and force {@link environmentFilter} (used by the wizard). */
    lockEnvironmentFilter?: boolean;
    defaultPartnerCode?: string;
    defaultSchemaVersion?: string;
    compact?: boolean;
    onDispatched?: () => void;
}

const As2EpcisDispatchPanel: FC<Props> = ({
    partners: partnersProp,
    environmentFilter = 'ALL',
    lockEnvironmentFilter = false,
    defaultPartnerCode,
    defaultSchemaVersion = '1.2',
    compact = false,
    onDispatched,
}) => {
    const [partners, setPartners] = useState<IAs2Partner[]>(partnersProp || []);
    const [epcisRows, setEpcisRows] = useState<EpcisRow[]>([]);
    const [loadingEpcis, setLoadingEpcis] = useState(false);
    const [partnerCode, setPartnerCode] = useState(defaultPartnerCode || '');
    const [epcisId, setEpcisId] = useState('');
    const [schemaVersion, setSchemaVersion] = useState(defaultSchemaVersion);
    const [sending, setSending] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [validationFailed, setValidationFailed] = useState(false);
    const [envFilter, setEnvFilter] = useState<EnvironmentFilter>(environmentFilter);
    const [prodConfirm, setProdConfirm] = useState<null | 'dispatch' | 'test'>(null);

    useEffect(() => {
        setEnvFilter(environmentFilter);
    }, [environmentFilter]);

    const loadEpcis = useCallback(async () => {
        setLoadingEpcis(true);
        try {
            const res: any = await epcisService.getEpcis({
                page: 0,
                size: 50,
                email: '',
                ndcCode: '',
                lotNumber: '',
                itemName: '',
                expiredOnly: false,
                startDate: '',
                endDate: '',
            });
            const content = (res.data?.epcis?.content || []) as EpcisRow[];
            setEpcisRows(content.filter((r) => r.id != null));
        } catch {
            toast.error('Could not load EPCIS records for the dropdown.', { autoClose: 5000 });
        } finally {
            setLoadingEpcis(false);
        }
    }, []);

    useEffect(() => {
        if (!partnersProp) {
            as2IntegrationService
                .listPartners()
                .then((r: any) => setPartners(r.data.as2Partners || []))
                .catch(() => {});
        } else {
            setPartners(partnersProp);
        }
    }, [partnersProp]);

    useEffect(() => {
        loadEpcis();
    }, [loadEpcis]);

    const partnerOptions = useMemo(() => {
        let list = partners.filter((p) => p.enabled !== false && p.code);
        if (envFilter === 'TEST') {
            list = list.filter((p) => p.environment === 'TEST' || p.code?.endsWith('_TEST'));
        } else if (envFilter === 'PRODUCTION') {
            list = list.filter(isProductionPartner);
        }
        return list;
    }, [partners, envFilter]);

    const selectedPartner = useMemo(
        () => partnerOptions.find((p) => p.code === partnerCode),
        [partnerOptions, partnerCode]
    );

    useEffect(() => {
        const stillValid = partnerCode && partnerOptions.some((p) => p.code === partnerCode);
        if (stillValid) return;
        const preferred =
            (defaultPartnerCode && partnerOptions.some((p) => p.code === defaultPartnerCode)
                ? defaultPartnerCode
                : '') ||
            partnerOptions.find((p) => p.code?.endsWith('_TEST'))?.code ||
            partnerOptions[0]?.code ||
            '';
        setPartnerCode(preferred);
    }, [defaultPartnerCode, partnerCode, partnerOptions]);

    const sendTestTemplate = () => {
        if (!partnerCode) {
            toast.error('Choose an AS2 partner.', { autoClose: 5000 });
            return;
        }
        if (isProductionPartner(selectedPartner)) {
            setProdConfirm('test');
            return;
        }
        void doSendTestTemplate();
    };

    const doSendTestTemplate = async () => {
        setSendingTest(true);
        try {
            const r: any = await as2IntegrationService.sendTest(partnerCode);
            const id = r.data?.as2Message?.id ?? r.data?.as2Message?.messageId;
            toast.success(
                `Test EPCIS queued for ${partnerCode}. Check AS2 Messages for MDN_RECEIVED.${id ? ` (#${id})` : ''}`,
                { autoClose: 10000 }
            );
            onDispatched?.();
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not send test EPCIS template.'), {
                autoClose: 8000,
            });
        } finally {
            setSendingTest(false);
        }
    };

    const dispatch = () => {
        if (!partnerCode) {
            toast.error('Choose an AS2 partner.', { autoClose: 5000 });
            return;
        }
        if (!epcisId) {
            toast.error('Choose an EPCIS record.', { autoClose: 5000 });
            return;
        }
        if (validationFailed) {
            toast.error('Outbound L4 validation failed — fix the document before dispatch.', { autoClose: 8000 });
            return;
        }
        if (isProductionPartner(selectedPartner)) {
            setProdConfirm('dispatch');
            return;
        }
        void doDispatch();
    };

    const doDispatch = async () => {
        setSending(true);
        try {
            await as2IntegrationService.dispatch({
                epcisId: Number(epcisId),
                partnerCode,
                schemaVersion,
            });
            toast.success(
                `EPCIS #${epcisId} queued for ${partnerCode}. Open AS2 Messages and watch for MDN_RECEIVED.`,
                { autoClose: 8000 }
            );
            onDispatched?.();
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not queue AS2 dispatch.'), { autoClose: 8000 });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className={`rounded-md border border-[#bd902e]/30 bg-amber-50/40 ${compact ? 'p-3' : 'p-4'}`}>
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h4 className="text-sm font-semibold text-slate-900">Send test EPCIS</h4>
                    <p className="mt-0.5 max-w-2xl text-xs text-slate-600">
                        Queues a database EPCIS record through OpenAS2, or use <strong>Send test template</strong>{' '}
                        (no DB row). For HTTPS only, use <strong>Test URL</strong> on the partner.
                    </p>
                </div>
                <Link
                    to={PRIVATEPATHS.Epcis}
                    className="shrink-0 text-xs font-semibold text-[#bd902e] hover:underline"
                >
                    Full EPCIS list →
                </Link>
            </div>
            {!lockEnvironmentFilter && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-slate-600">Partners:</span>
                    {(['ALL', 'TEST', 'PRODUCTION'] as EnvironmentFilter[]).map((f) => (
                        <button
                            key={f}
                            type="button"
                            onClick={() => setEnvFilter(f)}
                            className={`rounded-full border px-3 py-1 text-xs font-medium ${
                                envFilter === f
                                    ? 'border-[#bd902e] bg-[#bd902e] text-white'
                                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                        >
                            {f === 'ALL' ? 'All partners' : f === 'TEST' ? 'Test only' : 'Production only'}
                        </button>
                    ))}
                    {envFilter === 'PRODUCTION' && (
                        <span className="text-xs text-purple-800">Live sends — use with care.</span>
                    )}
                </div>
            )}
            <div className={`grid gap-3 ${compact ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2'}`}>
                <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">AS2 partner</label>
                    <select
                        className={inputClass}
                        value={partnerCode}
                        onChange={(e) => setPartnerCode(e.target.value)}
                    >
                        <option value="">— Select partner —</option>
                        {partnerOptions.map((p) => (
                            <option key={p.code} value={p.code!}>
                                {p.code} — {p.name}
                                {isProductionPartner(p) ? ' (PRODUCTION)' : ' (TEST)'}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">EPCIS record</label>
                    <select
                        className={inputClass}
                        value={epcisId}
                        disabled={loadingEpcis}
                        onChange={(e) => setEpcisId(e.target.value)}
                    >
                        <option value="">
                            {loadingEpcis
                                ? 'Loading records…'
                                : epcisRows.length === 0
                                  ? '— No EPCIS in database — use Send test template —'
                                  : '— Select EPCIS —'}
                        </option>
                        {epcisRows.map((row) => (
                            <option key={row.id} value={String(row.id)}>
                                {epcisOptionLabel(row)}
                                {row.jsonPathPresent ? '' : ' (legacy — may fail)'}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700">Schema version</label>
                    <select
                        className={inputClass}
                        value={schemaVersion}
                        onChange={(e) => setSchemaVersion(e.target.value)}
                    >
                        {SCHEMA_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={compact ? 'flex flex-wrap items-end gap-2' : 'sm:col-span-2 flex flex-wrap items-end gap-2'}>
                    <button
                        type="button"
                        disabled={sendingTest || !partnerCode}
                        onClick={sendTestTemplate}
                        className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
                    >
                        {sendingTest ? 'Sending…' : 'Send test template'}
                    </button>
                    <button
                        type="button"
                        disabled={sending || loadingEpcis || !epcisId || validationFailed}
                        onClick={dispatch}
                        className="rounded-md bg-[#bd902e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a67f28] disabled:opacity-50"
                    >
                        {sending ? 'Sending…' : 'Send via AS2'}
                    </button>
                    <button
                        type="button"
                        onClick={loadEpcis}
                        disabled={loadingEpcis}
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                        Refresh list
                    </button>
                </div>
            </div>
            {epcisId ? (
                <div className="mt-3">
                    <OutboundValidationPanel
                        epcisId={Number(epcisId)}
                        compact
                        onResult={(r) =>
                            setValidationFailed(r?.compliant === false || r?.status === 'FAILED')
                        }
                    />
                </div>
            ) : null}
            {!loadingEpcis && epcisRows.length === 0 && (
                <p className="mt-2 text-xs text-amber-800">
                    No EPCIS in the database yet. Use <strong>Send test template</strong> to test MDN without a DB row
                    ({partnerCode || 'TEST partner'}), or add EPCIS records from the{' '}
                    <Link to={PRIVATEPATHS.Epcis} className="font-semibold text-[#bd902e] hover:underline">
                        EPCIS list
                    </Link>
                    .
                </p>
            )}
            {prodConfirm && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                        <h4 className="text-base font-semibold text-slate-900">Send to production AS2?</h4>
                        <p className="mt-2 text-sm text-slate-700">
                            You are about to send {prodConfirm === 'dispatch' ? 'a live EPCIS record' : 'the test template'}{' '}
                            to the production partner{' '}
                            <strong>{selectedPartner?.code}</strong> ({selectedPartner?.name}). This reaches the live
                            trading partner endpoint.
                        </p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setProdConfirm(null)}
                                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const action = prodConfirm;
                                    setProdConfirm(null);
                                    if (action === 'dispatch') void doDispatch();
                                    else void doSendTestTemplate();
                                }}
                                className="rounded bg-purple-700 px-4 py-2 text-sm font-medium text-white hover:bg-purple-800"
                            >
                                Send to production
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default As2EpcisDispatchPanel;
